import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import "./CardGrid.style.css";

// Configurable Card Grid Component
// Props:
// - data: array of items to render
// - renderItem: function to render each item (receives item, index)
// - onItemClick: click handler function (optional)
// - spacing: spacing between grid items (default: 2)
// - size: grid size configuration object { xs, sm, md, lg, xl } (default: { xs: 6, sm: 6, md: 6, lg: 3 })
// - enableHorizontalScroll: boolean to enable horizontal scroll (default: false)
// - scrollHeight: height for horizontal scroll container (default: 'auto')
// - className: additional CSS classes
// - style: additional inline styles
// - containerProps: additional props for the container

// Header Configuration Props:
// - showHeader: boolean to show/hide header (default: false)
// - headerTitle: string for content type title (e.g., "Podcasts", "Audio", "Bhakti Sangeet")
// - showSeeAll: boolean to show/hide "See all" button (default: false)
// - seeAllText: custom text for see all button (default: "See all")
// - onSeeAllClick: function called when "See all" is clicked
// - headerClassName: additional CSS classes for header
// - headerStyle: additional inline styles for header

const CardGrid = ({
  data = [],
  renderItem,
  onItemClick,
  spacing = 2,
  size = { xs: 6, sm: 6, md: 6, lg: 3 },
  enableHorizontalScroll = false,
  scrollHeight = "auto",
  className = "",
  style = {},
  containerProps = {},
  type, // ebooks, podcasts, topics, bhaktiSangeet, information, devotionalNews
  // Header props
  showHeader = false,
  headerTitle = "",
  showSeeAll = false,
  seeAllText = "See all",
  onSeeAllClick,
  headerClassName = "",
  headerStyle = {},
  ...props
}) => {
  const handleItemClick = (item, index) => {
    if (onItemClick) {
      onItemClick(item, index);
    }
  };

  const handleSeeAllClick = () => {
    if (onSeeAllClick) {
      onSeeAllClick();
    }
  };

  // Header component
  const renderHeader = () => {
    if (!showHeader) return null;

    return (
      <Box
        className={`card-grid-header ${headerClassName}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
          ...headerStyle,
        }}
      >
        <Typography
          className="card-grid-header-title"
        //   sx={{
        //     fontWeight: 600,
        //     fontSize: { xs: "1.1rem", sm: "1.25rem" },
        //   }}
        >
          {headerTitle}
        </Typography>

        {showSeeAll && (
          <Typography
            className="card-grid-see-all-button"
            onClick={handleSeeAllClick}
            variant="body2"
          >
            {seeAllText}
          </Typography>
        )}
      </Box>
    );
  };

  // Render content inside Grid container
  const renderGridContent = () => (
    <Grid container spacing={spacing} {...containerProps} {...props}>
      {data.map((item, index) => (
        <Grid size={size} key={item.id || index}>
          {renderItem ? renderItem(item, index, handleItemClick) : null}
        </Grid>
      ))}
    </Grid>
  );

  // If horizontal scroll is enabled, wrap in a scrollable container
  if (enableHorizontalScroll) {
    return (
      <div className={`card-grid-container ${className}`} style={style}>
        {/* Header stays outside the scrollable area */}
        {renderHeader()}
        <Box
          className="card-grid-horizontal-scroll"
          style={{
            height: scrollHeight,
          }}
        >
          <Box className="card-grid-scroll-content">
            <Grid
              container
              spacing={spacing}
              wrap="nowrap"
              {...containerProps}
              {...props}
            >
              {data.map((item, index) => (
                <Grid
                  key={item.id || index}
                  size={size}
                  sx={{
                    width: {
                      xs: "200px",
                      sm: "200px",
                      md: type === "ebook" ? "200px" : "250px",
                      lg: type === "ebook" ? "200px" : "300px",
                    },
                    flexShrink: 0,
                  }}
                >
                  {renderItem ? renderItem(item, index, handleItemClick) : null}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </div>
    );
  }

  // Regular responsive grid
  return (
    <div className={`card-grid-container ${className}`} style={style}>
      {renderHeader()}
      <div className="card-grid">{renderGridContent()}</div>
    </div>
  );
};

export default CardGrid;
